import axiosClient from '../../../../api/axiosClient.js';
import { fetchPageData } from '../../../../api/utils.js';

async function getGeneralInfo() {
  const { data } = await axiosClient.get(`/api/general-info/`);
  return data;
}

export const generalInfoQuery = () => {
  return {
    queryKey: ['generalInfo'],
    queryFn: async () => getGeneralInfo(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000
  };
};

// Trimmed general-info payload for the instructor assignments page.
// Backend early-exits on `?assignment` and returns only the fields
// the page actually reads: games slimmed to 8 fields, courses reduced
// to id + game_limit, tournaments reduced to id + name, and sessions
// filtered to the current caller with only user/game/completed. Distinct
// queryKey so this cache stays separate from the full generalInfoQuery
// used by other consumers (game-play pages, etc.).
async function getAssignmentGeneralInfo() {
  const { data } = await axiosClient.get(`/api/general-info/?assignment`);
  return data;
}

export const generalInfoAssignmentQuery = () => {
  return {
    queryKey: ['generalInfo', 'assignment'],
    queryFn: async () => getAssignmentGeneralInfo(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000
  };
};

async function getTournament({tournamentId}) {
  const response = await axiosClient.get(`/api/tournaments/tournaments/${tournamentId}/`);

  return response.data;
}

export const tournamentQuery = ({tournamentId}) => {
  return {
    queryKey: ['instructorTournamentAssignments'],
    queryFn: async () => getTournament({tournamentId}),
    refetchOnWindowFocus: false
  };
};

export async function getTournamentsRounds({tournamentId}) {
  return fetchPageData(`/api/tournaments/rounds/?tournament_id=${tournamentId}`);
}

export const tournamentsRoundsQuery = ({tournamentId}) => {
  return {
    queryKey: ['tournamentsRounds', tournamentId],
    queryFn: async () => getTournamentsRounds({tournamentId}),
    refetchOnWindowFocus: false,
    enabled: !!tournamentId
  };
};

async function getCourses() {
  return fetchPageData(`/api/users/courses/`);
}

export const coursesQuery = () => {
  return {
    queryKey: ['courses'],
    queryFn: async () => getCourses(),
    refetchOnWindowFocus: false
  };
};

async function getGamesItems() {
  return fetchPageData(`/api/games/items/`);
}

export const gamesItemsQuery = () => {
  return {
    queryKey: ['gamesItems'],
    queryFn: async () => getGamesItems(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

async function getScenarios(name) {
  const url = name
    ? `/api/games/scenarios/?name=${name}`
    : `/api/games/scenarios/`;
  return fetchPageData(url);
}

export const scenariosQuery = ({name} = {}) => {
  return {
    queryKey: ['scenarios', name],
    queryFn: async () => getScenarios(name),
    refetchOnWindowFocus: false,
    cacheTime: 0,
    enabled: name !== undefined
  };
};

export async function createScenario({
  name,
  courses
}) {
  const { data } = await axiosClient.post(`api/games/scenarios/`, {
    name,
    is_published: true,
    courses
  });

  return data;
}

export async function editScenario({
  id,
  name
}) {
  const { data } = await axiosClient.patch(`api/games/scenarios/${id}/`, {
    name
  });

  return data;
}

export async function createGame({
  name,
  description,
  startTime,
  endTime,
  gameType,
  scenarioId,
  courseId
}) {
  const gameTypeMapping = {
    'Account Classification': 'ACC',
    'The Principles': 'PR',
    'Normal Balance': 'NOR',
    'Balanced Equation': 'BE',
    'Accounting Cycle': 'AC',
  };
  
  const gameTypeValue = gameTypeMapping[gameType] || '';
  const { data } = await axiosClient.post(`api/games/games/create/`, {
    name,
    description,
    start_time: startTime,
    end_time: endTime,
    points: 100,
    is_active: true,
    game_type: gameTypeValue,
    scenario: scenarioId,
    course: courseId
  });

  return data;
}

export async function createScenarioLevels({
  name,
  levelNumber,
  scenarios,
  description,
  design,
  scoring,
  numDecisionsQuestion,
  equation
}) {
  const { data } = await axiosClient.post(`/api/games/scenario-levels/`, {
    name,
    falling_speed: 2,
    level: levelNumber,
    show_score: true,
    show_value: true,
    scenarios,
    description,
    design,
    ...(scoring != null ? { scoring } : {}),
    ...(numDecisionsQuestion != null ? { num_decisions_question: numDecisionsQuestion } : {}),
    // `equation` is only sent for Balanced Equation level creates — the
    // BE modal auto-sets it to 'assets' for non-Increase-or-Decrease
    // cards, and pops the picker for Increase-or-Decrease. Non-BE game
    // types pass equation as null/undefined and it's omitted from the
    // request so the backend keeps its default (null).
    ...(equation != null ? { equation } : {})
  });

  return data;
}

export async function editScenarioLevel({
  scenarioLevelId,
  name,
  description,
  design,
  equation
}) {
  const { data } = await axiosClient.patch(`api/games/scenario-levels/${scenarioLevelId}/`, {
    name,
    description,
    design,
    // Only sent when the caller wants to change the equation field
    // (e.g. the future BE-modal equation-toggle). Left out otherwise
    // so the backend keeps whatever value is already stored.
    ...(equation !== undefined ? { equation } : {})
  });

  return data;
}

export async function createItemQuestions({
  gameType,
  order,
  accountItemId,
  explanation,
  scenarioLevelId
}) {
  const lastSlugPart = gameType === 'classification'
    ? 'item-questions'
    : gameType === 'principles'
      ? 'principles-questions'
      : 'questions';
  const { data } = await axiosClient.post(`api/${gameType}/${lastSlugPart}/`, {
    order,
    ...(gameType === 'equation' || gameType === 'principles' ? { transaction: accountItemId } : { account_item: accountItemId }),
    ...(explanation != null ? { explanation } : {}),
    scenario_level: scenarioLevelId
  });

  return data;
}

export async function createItemQuestionsBulk({
  gameType,
  questions,
  scenarioLevelId
}) {
  const lastSlugPart = gameType === 'classification'
    ? 'item-questions'
    : gameType === 'principles'
      ? 'principles-questions'
      : 'questions';
  const { data } = await axiosClient.post(`api/${gameType}/${lastSlugPart}/bulk/`,
    questions.map(q => ({
      order: q.order,
      ...(gameType === 'equation' || gameType === 'principles' ? { transaction: q.accountItemId } : { account_item: q.accountItemId }),
      ...(q.explanation != null ? { explanation: q.explanation } : {}),
      scenario_level: scenarioLevelId
    }))
  );

  return data;
}

export async function deleteItemQuestions({gameType, questionId}) {
  const lastSlugPart = gameType === 'classification' 
    ? 'item-questions'
    : gameType === 'principles'
      ? 'principles-questions'
      : 'questions';
  await axiosClient.delete(`api/${gameType}/${lastSlugPart}/${questionId}/`);
}

export async function createItemOptions({
  gameType,
  order,
  accountItemId,
  scenarioLevelId
}) {
  const lastSlugPart = gameType === 'classification' ? 'item-options' : 'options';
  const { data } = await axiosClient.post(`api/${gameType}/${lastSlugPart}/`, {
    order,
    account_item: accountItemId,
    scenario_level: scenarioLevelId
  });

  return data;
}

export async function createItemOptionsBulk({
  gameType,
  options,
  scenarioLevelId
}) {
  const lastSlugPart = gameType === 'classification' ? 'item-options' : 'options';
  const { data } = await axiosClient.post(`api/${gameType}/${lastSlugPart}/bulk/`,
    options.map(opt => ({
      order: opt.order,
      account_item: opt.accountItemId,
      scenario_level: scenarioLevelId
    }))
  );

  return data;
}

export async function deleteItemOptions({gameType = 'equation', optionId}) {
  const lastSlugPart = gameType === 'classification' 
    ? 'item-options'
    : 'options';
  await axiosClient.delete(`api/${gameType}/${lastSlugPart}/${optionId}/`);
}

export async function editGame({
  id,
  name,
  description,
  startTime,
  endTime
}) {
  const { data } = await axiosClient.patch(`api/games/games/update/${id}/`, {
    name,
    description,
    start_time: startTime,
    end_time: endTime,
  });

  return data;
}

export async function getScenarioLevels({scenarioId}) {
  return scenarioId ? fetchPageData(`/api/games/scenario-levels/?scenarios=${scenarioId}`) : [];
}

export const scenarioLevelsQuery = ({scenarioId}) => {
  return {
    queryKey: [`gameScenarioLevels`, scenarioId],
    queryFn: async () => getScenarioLevels({scenarioId}),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

export async function getInstructorScenarioLevels({scenarioId}) {
  return scenarioId ? fetchPageData(`/api/games/instructor/scenario-levels/?scenarios=${scenarioId}`) : [];
}

export const instructorScenarioLevelsQuery = ({scenarioId}) => {
  return {
    queryKey: [`instructorScenarioLevels`, scenarioId],
    queryFn: async () => getInstructorScenarioLevels({scenarioId}),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

export async function updateTournament({
  id,
  name
}) {
  const { data } = await axiosClient.patch(`api/tournaments/tournaments/${id}/`, {
    name
  });

  return data;
}

export async function deleteTournament({tournamentId}) {
  await axiosClient.delete(`api/tournaments/tournaments/${tournamentId}/`);
}

export async function deleteGame({gameId}) {
  await axiosClient.delete(`api/games/games/${gameId}/`);
}

export async function deleteScenarioLevel({scenarioLevelId}) {
  await axiosClient.delete(`api/games/scenario-levels/${scenarioLevelId}/`);
}

export async function scenarioAddCourse({scenarioId, courseId}) {
  const { data } = await axiosClient.put(`api/games/scenarios/${scenarioId}/add-course/`, { course_id: courseId });

  return data;
}

export async function getTransactions() {
  return fetchPageData(`/api/games/transactions/`);
}

export const transactionsQuery = () => {
  return {
    queryKey: ['transactionsThePrinciples'],
    queryFn: async () => getTransactions(),
    refetchOnWindowFocus: false,
    cacheTime: 0,
    enabled: false
  };
};

async function getCourseSetup() {
  const response = await axiosClient.get(`/api/games/scenario-groups/course-setup/`);
  return response.data.scenarios;
}

export const coursesSetupQuery = () => {
  return {
    queryKey: ['courseSetup'],
    queryFn: async () => getCourseSetup(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000
  };
};

async function getGame({id}) {
  const response = await axiosClient.get(`/api/games/games/${id}`);
  return response.data;
}

export const gameQuery = ({id}) => {
  return {
    queryKey: ['game', id],
    queryFn: async () => getGame({id}),
    refetchOnWindowFocus: false,
    cacheTime: 0,
    enabled: !!id
  };
};

export async function updateTournamentsRounds({ roundId, gameId, name }) {
  const payload = {};
  if (gameId) payload.game_id = gameId;
  if (name) payload.name = name;

  const { data } = await axiosClient.patch(`api/tournaments/rounds/${roundId}/`, payload);
  return data;
}

export async function getCourseMembers() {
  return fetchPageData(`/api/users/course-members/`);
}

export const courseMembersQuery = () => {
  return {
    queryKey: ['courseMembersAssignments'],
    queryFn: async () => getCourseMembers(),
    refetchOnWindowFocus: false
  };
};